import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import { cloudApi } from "../../api";
import "./PharmacyBranchProfile.css";

const SkeletonProfileCard = () => (
  <div className="pbp-card pbp-skeleton-card">
    {[...Array(9)].map((_, i) => (
      <div key={i} className="pbp-skeleton-box"></div>
    ))}
    <div className="pbp-skeleton-btns">
      <div className="pbp-skeleton-btn"></div>
      <div className="pbp-skeleton-btn"></div>
    </div>
  </div>
);

const PharmacyBranchProfile = ({ branchId }) => {
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // ── Phone number change state ──
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneSuccess, setPhoneSuccess] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);

  // ── Fetch branch profile ──
  useEffect(() => {
    const fetchBranchProfile = async () => {
      try {
        const { data } = await cloudApi.get(`/users/pharmacy/branch/${branchId}/profile`);
        setBranch(data);
      } catch (err) {
        console.error("❌ Error loading branch profile:", err);
        setError("Failed to load branch profile.");
      } finally {
        setLoading(false);
      }
    };
    if (branchId) fetchBranchProfile();
  }, [branchId]);

  // ── Delete branch ──
  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this branch?")) {
      try {
        await cloudApi.delete(`/users/pharmacy/branch/${branchId}`);
        alert("Branch deleted successfully.");
        navigate("/PharmacyBranches");
      } catch (err) {
        console.error("Delete error:", err);
        alert("Failed to delete branch.");
      }
    }
  };

  // ── Reset password ──
  const handleResetPassword = () => {
    navigate(`/PharmacyBranchPasswordReset/${branchId}`);
  };

  // ── Edit profile ──
  const handleEdit = () => {
    navigate(`/UpdatePharmacyBranchProfile/${branchId}`);
  };

  // ── Phone modal handlers ──
  const handleOpenPhoneModal = () => {
    setNewPhoneNumber("");
    setPhoneError("");
    setPhoneSuccess("");
    setShowPhoneModal(true);
  };

  const handleClosePhoneModal = () => {
    setShowPhoneModal(false);
    setNewPhoneNumber("");
    setPhoneError("");
    setPhoneSuccess("");
  };

  const handlePhoneChange = async () => {
    setPhoneError("");
    setPhoneSuccess("");

    // ── Frontend validation ──
    if (!newPhoneNumber.trim()) {
      setPhoneError("Please enter a phone number.");
      return;
    }
    if (!/^\d+$/.test(newPhoneNumber)) {
      setPhoneError("Phone number must contain only digits.");
      return;
    }
    if (newPhoneNumber.length < 10 || newPhoneNumber.length > 15) {
      setPhoneError("Phone number must be between 10 and 15 digits.");
      return;
    }
    if (newPhoneNumber === branch?.phone_number) {
      setPhoneError("New phone number is the same as the current one.");
      return;
    }

    try {
      setPhoneLoading(true);

      // ── FormData because backend expects Form(...) ──
      const formData = new FormData();
      formData.append("new_phone_number", newPhoneNumber);

      await cloudApi.put(
        `/users/pharmacy/branch/${branchId}/phone-number`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // ── Update local state so profile reflects new number immediately ──
      setBranch((prev) => ({ ...prev, phone_number: newPhoneNumber }));
      setPhoneSuccess("Phone number updated successfully.");

      setTimeout(() => {
        handleClosePhoneModal();
      }, 1500);
    } catch (err) {
      console.error("Phone change error:", err);
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") setPhoneError(detail);
      else if (Array.isArray(detail)) setPhoneError(detail.map((e) => e.msg).join(", "));
      else setPhoneError("Failed to update phone number. Please try again.");
    } finally {
      setPhoneLoading(false);
    }
  };

  if (error) return <p className="pbp-error">{error}</p>;
  if (!branch && !loading) return <p className="pbp-no-data">No branch data found.</p>;

  return (
    <div className="pbp-page-container">
      <Sidebar />
      <div className="pbp-content">
        <h2 className="pbp-title">Branch Profile</h2>

        {loading ? (
          <SkeletonProfileCard />
        ) : (
          <div className="pbp-card">
            <p><strong>Branch Name:</strong> {branch.branch_name || "—"}</p>
            <p><strong>User Name:</strong> {branch.user_name || "—"}</p>
            <p><strong>Address:</strong> {branch.branch_address || "—"}</p>
            <p><strong>Opening Hours:</strong> {branch.opening_hours || "—"}</p>
            <p><strong>Supervising Pharmacist:</strong> {branch.supervising_pharmacist_name || "—"}</p>
            <p><strong>Pharmacist License:</strong> {branch.supervising_pharmacist_license || "—"}</p>
            <p><strong>Verification Status:</strong> {branch.verification_status || "—"}</p>

            {/* ── Phone number row with inline change button ── */}
            <p>
              <strong>Phone Number:</strong>{" "}
              {branch.phone_number || "—"}
              <button
                onClick={handleOpenPhoneModal}
                style={{
                  marginLeft: "0.75rem",
                  padding: "0.2rem 0.65rem",
                  fontSize: "0.78rem",
                  background: "#17a2b8",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                📱 Change
              </button>
            </p>

            <p><strong>City:</strong> {branch.city || "—"}</p>
            <p><strong>State:</strong> {branch.state || "—"}</p>
            <p><strong>Country:</strong> {branch.country || "—"}</p>
            <p><strong>Created At:</strong> {branch.created_at ? new Date(branch.created_at).toLocaleString() : "—"}</p>

            <div className="pbp-btn-group">
              <button className="pbp-edit-btn" onClick={handleEdit}>Edit</button>
              <button className="pbp-delete-btn" onClick={handleDelete}>Delete</button>
              <button className="pbp-reset-btn" onClick={handleResetPassword}>Reset Password</button>
            </div>
          </div>
        )}
      </div>

      {/* ===== PHONE NUMBER CHANGE MODAL ===== */}
      {showPhoneModal && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleClosePhoneModal}
        >
          <div
            style={{
              background: "white", borderRadius: "10px", padding: "2rem",
              width: "100%", maxWidth: "420px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>📱 Change Branch Phone Number</h3>
              <button
                onClick={handleClosePhoneModal}
                style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#666" }}
              >
                ✕
              </button>
            </div>

            {/* Current phone */}
            <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: "1.2rem" }}>
              Current number: <strong>{branch?.phone_number || "—"}</strong>
            </p>

            {/* Input */}
            <label style={{ fontWeight: 600, fontSize: "0.9rem", display: "block", marginBottom: "0.4rem" }}>
              New Phone Number *
            </label>
            <input
              type="tel"
              value={newPhoneNumber}
              onChange={(e) => { setNewPhoneNumber(e.target.value); setPhoneError(""); }}
              placeholder="e.g. 08012345678"
              disabled={phoneLoading}
              autoFocus
              style={{
                width: "100%", padding: "0.75rem",
                border: `1px solid ${phoneError ? "#dc3545" : "#ddd"}`,
                borderRadius: "6px", fontSize: "1rem", boxSizing: "border-box",
              }}
            />
            <small style={{ color: "#888", fontSize: "0.8rem", display: "block", marginTop: "0.3rem" }}>
              10–15 digits, numbers only.
            </small>

            {/* Error / success */}
            {phoneError && (
              <div style={{ marginTop: "0.75rem", padding: "0.6rem 0.85rem", background: "#fff5f5", border: "1px solid #dc3545", borderRadius: "6px", color: "#dc3545", fontSize: "0.875rem" }}>
                {phoneError}
              </div>
            )}
            {phoneSuccess && (
              <div style={{ marginTop: "0.75rem", padding: "0.6rem 0.85rem", background: "#f0fff4", border: "1px solid #28a745", borderRadius: "6px", color: "#28a745", fontSize: "0.875rem" }}>
                ✅ {phoneSuccess}
              </div>
            )}

            {/* Modal actions */}
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", justifyContent: "flex-end" }}>
              <button
                onClick={handleClosePhoneModal}
                disabled={phoneLoading}
                style={{ padding: "0.6rem 1.2rem", border: "1px solid #ccc", borderRadius: "6px", background: "white", cursor: "pointer", fontSize: "0.9rem" }}
              >
                Cancel
              </button>
              <button
                onClick={handlePhoneChange}
                disabled={phoneLoading || !newPhoneNumber.trim()}
                style={{
                  padding: "0.6rem 1.4rem", border: "none", borderRadius: "6px",
                  background: phoneLoading ? "#aaa" : "#17a2b8",
                  color: "white", cursor: phoneLoading ? "not-allowed" : "pointer",
                  fontSize: "0.9rem", fontWeight: 600,
                }}
              >
                {phoneLoading ? "Updating..." : "✓ Update Phone"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacyBranchProfile;